/** Portable adapter around the unmodified iPad WebKit knowledgeMap bridge. */
export function installKnowledgeMapHost(config: { token: string; parentOrigin: string }) {
  const w = window as any;
  let currentMapID = '';
  let rendering = false;
  let queue = Promise.resolve();
  const send = (data: object) => parent.postMessage({ channel: 'reados-knowledge-map-v1', token: config.token, ...data }, config.parentOrigin);
  const action = (data: { action?: string; [key: string]: unknown }) => {
    // A resize while the first fit/restore is pending can briefly expose the
    // identity transform. Never persist that intermediate viewport.
    if (rendering && data.action === 'viewport') return;
    send({ type: 'action', mapID: currentMapID, ...data });
  };
  w.webkit = { messageHandlers: { knowledgeMap: { postMessage: action } } };
  const nodeFor = (target: EventTarget | null) => target instanceof Element ? target.closest<SVGGElement>('g.markmap-node') : null;
  const payloadFor = (group: SVGGElement | null) => (group as any)?.__data__?.payload;
  const showMenu = (group: SVGGElement | null, x?: number, y?: number) => {
    const payload = payloadFor(group);
    if (!payload?.id || payload.isSyntheticRoot || !group) return;
    const rect = group.getBoundingClientRect();
    action({ action: 'showMenu', nodeID: payload.id,
      normalizedX: Math.max(0, Math.min(1, (x ?? rect.x + rect.width / 2) / innerWidth)),
      normalizedY: Math.max(0, Math.min(1, (y ?? rect.y + rect.height / 2) / innerHeight)) });
  };
  const decorate = () => {
    for (const group of document.querySelectorAll<SVGGElement>('g.markmap-node')) {
      const payload = payloadFor(group);
      if (!payload || payload.isSyntheticRoot) continue;
      group.setAttribute('tabindex', '0');
      group.setAttribute('role', 'button');
      group.setAttribute('aria-label', payload.title || '知识节点');
      group.setAttribute('aria-haspopup', 'menu');
      group.classList.toggle('km-selected', payload.isSelected === true);
      const badge = group.querySelector<HTMLElement>('.km-answer-badge');
      if (badge) badge.tabIndex = 0;
      const circle = group.querySelector('circle');
      if (circle) {
        circle.setAttribute('tabindex', '0');
        circle.setAttribute('role', 'button');
        circle.setAttribute('aria-label', `展开或收起 ${payload.title || '子节点'}`);
      }
    }
  };
  document.addEventListener('contextmenu', event => {
    const group = nodeFor(event.target);
    if (!group || payloadFor(group)?.isSyntheticRoot) return;
    event.preventDefault();
    showMenu(group, event.clientX, event.clientY);
  });
  document.addEventListener('keydown', event => {
    const group = nodeFor(event.target);
    const payload = payloadFor(group);
    if (!payload?.id || payload.isSyntheticRoot) return;
    if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
      event.preventDefault(); showMenu(group); return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const target = event.target as Element;
    if (target.closest('circle')) { target.dispatchEvent(new MouseEvent('click', { bubbles: true })); return; }
    if (target.closest('.km-answer-badge')) action({ action: 'showAnswers', nodeID: payload.id });
    else if (payload.pageNumber) action({ action: 'jump', nodeID: payload.id });
    else showMenu(group);
  });
  window.addEventListener('message', event => {
    if (event.source !== parent || event.origin !== config.parentOrigin) return;
    const data = event.data;
    if (data?.channel !== 'reados-knowledge-map-v1' || data.token !== config.token) return;
    if (data.type !== 'render' && data.type !== 'fit') return;
    queue = queue.then(async () => {
      if (data.type === 'fit') { await w.fitKnowledgeMap(); return; }
      rendering = true;
      try {
      // Let the opaque iframe settle its final flex geometry before the
      // original runtime computes its first fit/restore transform. The parent
      // status/error UI is an absolute overlay, so this does not resize the
      // Markmap canvas while the two frames elapse.
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      currentMapID = data.payload.mapID;
      w.setKnowledgeMapColorScheme(data.colorScheme);
      const rendered = await w.renderKnowledgeMap(data.payload, data.viewport, {});
      // The copied iPad runtime deliberately returns null when a newer
      // layoutRevision supersedes the current render (resize/close-reopen can
      // do this while Markmap has already painted). A null result is not a
      // render failure when the SVG has nodes; wait a few animation frames so
      // the old layout can settle before surfacing an actual error.
      if (!rendered) {
        let hasGraphics = false;
        for (let frame = 0; frame < 8; frame += 1) {
          await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
          hasGraphics = Boolean(document.querySelector('#mindmap g.markmap-node, #mindmap path.markmap-link'));
          if (hasGraphics) break;
        }
        if (!hasGraphics) throw new Error('知识地图渲染器未能生成视图');
      }
      decorate();
      send({ type: 'rendered' });
      } finally { rendering = false; }
    }).catch((error: unknown) => send({ type: 'error', message: error instanceof Error ? error.message : '知识地图加载失败' }));
  });
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof w.renderKnowledgeMap !== 'function' || !w.markmap?.Markmap) { send({ type: 'error', message: '知识地图资源未能加载' }); return; }
    const svg = document.getElementById('mindmap')!;
    svg.setAttribute('aria-label', '知识地图');
    new MutationObserver(decorate).observe(svg, { childList: true, subtree: true });
    send({ type: 'ready' });
  }, { once: true });
}
